// React
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Redux
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { uploadChargebacks } from "@/DataPages/api/chargebacks";
import { useQueryClient } from "@tanstack/react-query";

import UploadDataButton from "@/Layouts/components/Buttons/UploadDataButton";
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import ChargebacksTable from "@DataPages/components/Chargebacks/Tables/ChargebacksTable.jsx";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";

import { getSalesInvoices } from "@/DataPages/api/chargebacks/queries";

// Hooks
import { usePaginatedResource } from "@/Common/hooks/Pagination";
import { buildGetNextPageParam, DEFAULT_CURSOR, PAGE_SIZE } from "@/Common/hooks/Pagination/helpers";

// MUI
import { Grid, Paper, Skeleton, Stack, Typography } from "@mui/material";

// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";
import { errorLogger } from "@/Common/helpers/logger";
import { useBuildTableFilters } from "@/Common/hooks/Query/useBuildTableFilters";
import actionsList from "@/Constants/actionsList";
import { isEmpty } from "lodash";

export default function ChargebacksModel({ commissionCycle, locationId, showUpload }) {
	const commissionCycleId = commissionCycle?._id;
	const queryClient = useQueryClient();
	const { useAuth } = useAuthorization();
	const uploadChargebacksReport = useAuth(actionsList.UPLOAD_CHARGEBACKS_REPORT);

	const [uploading, setUploading] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();

	const filters = useBuildTableFilters({
		invalid: { field: "invalid", operator: "equals" },
	});

	const handleFilterModelChange = useCallback(
		(filterModel) => {
			const newSearchParams = new URLSearchParams(searchParams);
			const filterItems = filterModel.items || [];
			if (isEmpty(filterItems)) {
				setSearchParams({});
				return;
			}

			const hasInvalidFilter = filterItems.some((item) => item.field === "invalid");
			if (!hasInvalidFilter) {
				newSearchParams.delete("invalid");
			}

			setSearchParams(newSearchParams);
		},
		[searchParams, setSearchParams]
	);

	const {
		data: chargebacks,
		isFetching,
		hasNextPage,
		status,
		refetch,
	} = usePaginatedResource({
		queryKey: ["chargebacks", locationId, commissionCycleId],
		queryFn: async ({ pageParam }) => {
			const cursor = pageParam;
			const paginatedResponse = await getSalesInvoices(
				{ locationId, commissionCycleId },
				{ first: cursor.first, after: cursor.after }
			);
			return paginatedResponse;
		},
		enabled: Boolean(commissionCycleId && locationId),
		initialPageParam: DEFAULT_CURSOR,
		getNextPageParam: buildGetNextPageParam(PAGE_SIZE),
		fetchAllPages: true,
		structuralSharing: false, // Force new reference on every refetch
	});

	const isLoading = status === "pending";

	// Subscriptions
	const { getSubscription } = useSubscriptions();
	const chargebacksSubscriptions = getSubscription(["salesInvoices", locationId, commissionCycleId]);
	const handleChargebacksSubscription = useCallback(
		async (result) => {
			setUploading(false);
			const eventCommissionCycleId = result?.data?.onCreateSalesInvoices?.commissionCycleId;
			if (!eventCommissionCycleId) return;
			await refetch();
		},
		[refetch]
	);
	chargebacksSubscriptions?.subscriptions?.onCreateSalesInvoices?.setCallback(handleChargebacksSubscription);

	const handleUploadFile = (event) => {
		const file = event.target.files[0] || null;
		try {
			const acceptedTypes = [
				"csv",
				"text/csv",
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				"application/vnd.ms-excel",
			];

			if (acceptedTypes.includes(file.type)) {
				if (locationId && commissionCycleId) {
					const upload = uploadChargebacks(locationId, commissionCycleId, file);
					upload.then(() => {
						event.target.value = null;
					});
				}

				setUploading(true);
			}
		} catch (error) {
			errorLogger(error);
		}
	};

	return (
		<>
			<Paper elevation={0} sx={{ position: "relative", backgroundColor: "transparent" }}>
				<Typography sx={{ lineHeight: "1" }} className={"page-title"}>
					<>
						{isLoading || uploading ? (
							<Skeleton sx={{ borderRadius: "6px" }} variant="rectangular" width={280} height={30} />
						) : (
							<>{commissionCycle && `${commissionCycle.title} Chargebacks`}</>
						)}
					</>
				</Typography>

				{showUpload && (
					<>
						{isLoading || uploading ? (
							<Stack sx={{ position: "absolute", right: 0, top: 0 }}>
								<Skeleton sx={{ borderRadius: "6px" }} variant="rectangular" width={178} height={30} />
							</Stack>
						) : (
							<UploadDataButton
								title={"Upload Chargebacks Report"}
								className={
									"!bg-primary !shadow-none !rounded-md [&.Mui-disabled]:opacity-50 [&.Mui-disabled]:!text-white !px-[12px] !py-[6px] min-w-[120px]\
											[&_.MuiTypography-root]:!font-sans [&_.MuiTypography-root]:!font-normal [&_.MuiTypography-root]:!text-sm/[normal] z-10"
								}
								accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
								disabled={!uploadChargebacksReport}
								onChange={handleUploadFile}
								labelSx={{ position: "absolute", right: 0, top: 0 }}
							/>
						)}
					</>
				)}
			</Paper>
			<Grid container mt={2}>
				<Grid item xs={12}>
					{isLoading || uploading ? (
						<TableLoader content rows={7} toolbar buttons={2} />
					) : (
						<>
							{Boolean(chargebacks?.length) && (
								<ChargebacksTable data={chargebacks} filters={filters} onFilterModelChange={handleFilterModelChange} />
							)}
							{isFetching && hasNextPage && (
								<LoadingSpinner label="Chargebacks are still loading..." className="items-center" />
							)}
						</>
					)}
				</Grid>
			</Grid>

			{!Boolean(chargebacks?.length) && !Boolean(isLoading || uploading) && (
				<EmptyBody
					image={commissionCycle ? emptyTable : empty}
					title={commissionCycle ? "No Chargebacks to show" : "No Chargebacks selected"}
					bodyHeight={"490px"}
					sx={{
						boxShadow: "none !important",
						maxHeight: "530px",
						marginTop: "-16px",
					}}
				/>
			)}
		</>
	);
}
